import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  DollarSign,
  Layers,
  Percent,
  RefreshCw,
  TrendingUp,
  Users
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

interface AnalyticsData {
  totalRevenue: number;
  monthlyRevenue: Array<{ month: string; revenue: number }>;
  revenueByPlan: Array<{ plan: string; revenue: number; count: number }>;
  schoolGrowth: Array<{ month: string; count: number }>;
  subscriptionStats: {
    active: number;
    trial: number;
    suspended: number;
    expired: number;
  };
  revenueEstimates: {
    annual_revenue: number;
    monthly_revenue: number;
  };
}

const formatCurrency = (value: number | undefined) =>
  new Intl.NumberFormat("en-ZM", {
    style: "currency",
    currency: "ZMW",
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const GrowthAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string>("");

  const fetchAnalytics = async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("superadmin/analytics"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data || null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const totals = useMemo(() => {
    const subscriptionStats = data?.subscriptionStats || { active: 0, trial: 0, suspended: 0, expired: 0 };
    const totalInstances =
      Number(subscriptionStats.active || 0) +
      Number(subscriptionStats.trial || 0) +
      Number(subscriptionStats.suspended || 0) +
      Number(subscriptionStats.expired || 0);
    const active = Number(subscriptionStats.active || 0);
    const trial = Number(subscriptionStats.trial || 0);
    const churnBase = totalInstances || 1;
    const conversion = active + trial > 0 ? (active / (active + trial)) * 100 : 0;
    const churnProbability = ((Number(subscriptionStats.suspended || 0) + Number(subscriptionStats.expired || 0)) / churnBase) * 100;
    const latestMonthRevenue = data?.monthlyRevenue?.[data.monthlyRevenue.length - 1]?.revenue || 0;
    const previousMonthRevenue = data?.monthlyRevenue?.[data.monthlyRevenue.length - 2]?.revenue || 0;
    const revenueDelta = previousMonthRevenue > 0 ? ((latestMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 : 0;
    const latestGrowth = data?.schoolGrowth?.[data.schoolGrowth.length - 1]?.count || 0;
    const previousGrowth = data?.schoolGrowth?.[data.schoolGrowth.length - 2]?.count || 0;
    const growthDelta = previousGrowth > 0 ? ((latestGrowth - previousGrowth) / previousGrowth) * 100 : latestGrowth > 0 ? 100 : 0;

    return {
      totalInstances,
      active,
      trial,
      conversion,
      churnProbability,
      revenueDelta,
      growthDelta
    };
  }, [data]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-teal-500/20 border-t-teal-600" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Generating growth matrix...</p>
        </div>
      </div>
    );
  }

  const monthlyRevenue = data?.monthlyRevenue || [];
  const schoolGrowth = data?.schoolGrowth || [];
  const maxRevenue = Math.max(...monthlyRevenue.map((entry) => Number(entry.revenue || 0)), 1);
  const maxGrowth = Math.max(...schoolGrowth.map((entry) => Number(entry.count || 0)), 1);

  return (
    <div className="dashboard-container">
      <main className="dashboard-main bg-slate-50/20 p-8">
        <header className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <TrendingUp size={18} className="text-teal-600" />
              <span className="rounded-full bg-teal-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-teal-600">
                Global Intelligence
              </span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">
              Growth <span className="italic text-teal-600">Analytics</span>
            </h1>
            <p className="mt-2 max-w-2xl font-medium text-slate-500">
              Financial projections, expansion metrics, and platform adoption across all school instances.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchAnalytics(true)}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </header>

        {error ? (
          <div className="mb-8 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-bold text-rose-600">
            {error}
          </div>
        ) : null}

        <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <MetricsCard
            title="Estimated ARR"
            value={formatCurrency(data?.revenueEstimates?.annual_revenue)}
            change={`${totals.revenueDelta >= 0 ? "+" : ""}${totals.revenueDelta.toFixed(1)}%`}
            isPositive={totals.revenueDelta >= 0}
            icon={<DollarSign size={22} />}
            color="emerald"
            helper="Compared with the previous recorded revenue month"
          />
          <MetricsCard
            title="Active Instances"
            value={totals.active}
            change={`${totals.growthDelta >= 0 ? "+" : ""}${totals.growthDelta.toFixed(1)}%`}
            isPositive={totals.growthDelta >= 0}
            icon={<Building2 size={22} />}
            color="indigo"
            helper={`${totals.totalInstances} total schools in platform state tracking`}
          />
          <MetricsCard
            title="Global Conversion"
            value={`${totals.conversion.toFixed(1)}%`}
            change={`${totals.trial} trial school(s)`}
            isPositive={totals.conversion >= 50}
            icon={<Percent size={22} />}
            color="teal"
            helper="Active schools divided by active + trial schools"
          />
          <MetricsCard
            title="Churn Exposure"
            value={`${totals.churnProbability.toFixed(1)}%`}
            change={`${Number(data?.subscriptionStats?.suspended || 0) + Number(data?.subscriptionStats?.expired || 0)} at-risk school(s)`}
            isPositive={totals.churnProbability <= 10}
            icon={<Activity size={22} />}
            color="rose"
            helper="Suspended and expired schools as a share of total tracked schools"
          />
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/50">
            <h3 className="mb-8 flex items-center gap-2 text-lg font-bold text-slate-800">
              <Layers size={18} className="text-slate-400" /> Plan Distribution
            </h3>
            <div className="space-y-6">
              {(data?.revenueByPlan || []).length ? (
                (data?.revenueByPlan || []).map((plan) => {
                  const planShare = totals.totalInstances ? (Number(plan.count || 0) / totals.totalInstances) * 100 : 0;
                  return (
                    <div key={plan.plan || "unassigned"}>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-bold uppercase tracking-wider text-slate-600">{plan.plan || "unassigned"}</span>
                        <span className="text-sm font-bold text-slate-900">{plan.count} school(s)</span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-slate-50">
                        <div
                          className="h-full rounded-full bg-teal-500 shadow-[0_0_10px_rgba(20,184,166,0.3)] transition-all duration-700"
                          style={{ width: `${Math.max(planShare, plan.count ? 8 : 0)}%` }}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                        <span>{planShare.toFixed(1)}% of tracked schools</span>
                        <span>{formatCurrency(plan.revenue)}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No plan distribution data available yet.
                </div>
              )}
            </div>

            <div className="mt-12 rounded-3xl border border-slate-100/50 bg-slate-50 p-6">
              <p className="mb-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Instance Health State</p>
              <div className="grid grid-cols-2 gap-4">
                <HealthState label="Active" value={data?.subscriptionStats?.active || 0} tone="bg-emerald-500" />
                <HealthState label="Trialing" value={data?.subscriptionStats?.trial || 0} tone="bg-amber-500" />
                <HealthState label="Suspended" value={data?.subscriptionStats?.suspended || 0} tone="bg-rose-500" />
                <HealthState label="Expired" value={data?.subscriptionStats?.expired || 0} tone="bg-slate-400" />
              </div>
            </div>
          </div>

          <div className="space-y-8 lg:col-span-2">
            <div className="flex h-[320px] flex-col rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/50">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Monthly Revenue Stream</h3>
                  <p className="mt-1 text-sm text-slate-500">Derived from the current analytics feed.</p>
                </div>
                <div className="rounded-lg bg-teal-50 px-3 py-1 text-[10px] font-bold uppercase text-teal-600">ZMW</div>
              </div>
              {monthlyRevenue.length ? (
                <div className="flex flex-1 items-end gap-2 px-4 pb-4">
                  {monthlyRevenue.map((entry, index) => (
                    <div key={`${entry.month}-${index}`} className="group relative flex-1">
                      <div
                        className="w-full rounded-t-lg bg-teal-400/30 transition-all group-hover:bg-teal-500"
                        style={{ height: `${(Number(entry.revenue || 0) / maxRevenue) * 180}px` }}
                      />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-bold text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100">
                        {formatCurrency(entry.revenue)}
                      </div>
                      <div className="mt-3 text-center text-[10px] font-bold uppercase tracking-tighter text-slate-400">{entry.month}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                  No monthly revenue history is available yet.
                </div>
              )}
            </div>

            <div className="flex h-[320px] flex-col rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/50">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Expansion Velocity</h3>
                  <p className="mt-1 text-sm text-slate-500">New school growth over the tracked months.</p>
                </div>
                <div className="rounded-lg bg-indigo-50 px-3 py-1 text-[10px] font-bold uppercase text-indigo-600">Schools</div>
              </div>
              {schoolGrowth.length ? (
                <div className="flex flex-1 items-end gap-2 px-4 pb-4">
                  {schoolGrowth.map((entry, index) => (
                    <div key={`${entry.month}-${index}`} className="group relative flex-1">
                      <div
                        className="w-full rounded-t-lg bg-indigo-400/30 transition-all group-hover:bg-indigo-500"
                        style={{ height: `${(Number(entry.count || 0) / maxGrowth) * 180}px` }}
                      />
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 rounded bg-indigo-600 px-2 py-1 text-[10px] font-bold text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100">
                        +{entry.count}
                      </div>
                      <div className="mt-3 text-center text-[10px] font-bold uppercase tracking-tighter text-slate-400">{entry.month}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                  No school growth trend is available yet.
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-8 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <article className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/50">
            <h3 className="mb-6 text-lg font-bold text-slate-800">Revenue by Plan</h3>
            <div className="space-y-4">
              {(data?.revenueByPlan || []).length ? (
                (data?.revenueByPlan || []).map((plan) => (
                  <div key={`revenue-${plan.plan}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold uppercase tracking-wide text-slate-800">{plan.plan || "Unassigned"}</p>
                        <p className="mt-1 text-xs text-slate-500">{plan.count} school(s) on this tier</p>
                      </div>
                      <p className="text-base font-black text-slate-900">{formatCurrency(plan.revenue)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Revenue-by-plan data is not available.
                </div>
              )}
            </div>
          </article>

          <article className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/50">
            <h3 className="mb-6 text-lg font-bold text-slate-800">Platform Revenue Snapshot</h3>
            <div className="space-y-4">
              <SnapshotRow
                label="Monthly revenue estimate"
                value={formatCurrency(data?.revenueEstimates?.monthly_revenue)}
              />
              <SnapshotRow
                label="Annual revenue estimate"
                value={formatCurrency(data?.revenueEstimates?.annual_revenue)}
              />
              <SnapshotRow
                label="Reported total revenue"
                value={formatCurrency(data?.totalRevenue)}
              />
              <SnapshotRow
                label="Tracked schools"
                value={String(totals.totalInstances)}
              />
            </div>
          </article>
        </section>
      </main>
    </div>
  );
};

const HealthState = ({ label, value, tone }: { label: string; value: number; tone: string }) => (
  <div className="flex items-center gap-2">
    <div className={`h-2 w-2 rounded-full ${tone}`} />
    <span className="text-xs font-bold text-slate-700">
      {label}: {value}
    </span>
  </div>
);

const SnapshotRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
    <span className="text-sm font-semibold text-slate-600">{label}</span>
    <span className="text-sm font-black text-slate-900">{value}</span>
  </div>
);

const MetricsCard = ({
  title,
  value,
  change,
  isPositive,
  icon,
  color,
  helper
}: {
  title: string;
  value: string | number;
  change: string;
  isPositive: boolean;
  icon: React.ReactNode;
  color: "emerald" | "indigo" | "teal" | "rose";
  helper: string;
}) => {
  const colors: Record<string, string> = {
    emerald: "border-emerald-100 bg-emerald-50/50 text-emerald-600",
    indigo: "border-indigo-100 bg-indigo-50/50 text-indigo-600",
    teal: "border-teal-100 bg-teal-50/50 text-teal-600",
    rose: "border-rose-100 bg-rose-50/50 text-rose-600"
  };

  return (
    <div className="group rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-xl shadow-slate-200/40 transition-all hover:-translate-y-1">
      <div className="mb-6 flex items-start justify-between">
        <div className={`rounded-2xl border p-4 transition-transform group-hover:scale-110 ${colors[color]}`}>{icon}</div>
        <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold ${isPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
          {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {change}
        </div>
      </div>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>
      <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      <p className="mt-3 text-xs leading-6 text-slate-500">{helper}</p>
    </div>
  );
};

export default GrowthAnalytics;
